import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import {
  ChartData,
  Metric,
  MetricsData,
} from '@features/campaigns/performance/components/ImpressionsDistribution/types.ts';
import { getFormattedValue } from '@features/campaigns/performance/components/ImpressionsDistribution/utils.ts';
import { formatFullPercentage } from '@features/campaigns/performance/shared/utils/formatPercentage.ts';
import { i18nNameSpace } from '@shared/consts/i18n.ts';

import styles from './ChartLegend.module.scss';

interface ChartLegendProps {
  chartData: ChartData;
  metricsData: MetricsData | null;
  shouldPartiallyHideInteractiveMetrics: boolean;
}

function ChartLegend({ chartData, metricsData, shouldPartiallyHideInteractiveMetrics }: ChartLegendProps) {
  const { t } = useTranslation(i18nNameSpace.CAMPAIGNS);

  return (
    <div className={styles.legendWrapper}>
      {chartData?.labels?.map((label, index) => {
        const legendItemValue = chartData?.datasets?.[0]?.data?.[index] ? chartData.datasets[0].data[index] : 0;
        const formattedLegendItemValue = formatFullPercentage(legendItemValue);
        const legendItemColor = chartData?.datasets?.[0]?.backgroundColor?.[index];

        const metrics: Metric[] | null =
          metricsData?.[label.toLowerCase().replaceAll(' ', '') as keyof typeof metricsData] ?? null;
        return (
          <div key={label}>
            <div className={styles.legendItem}>
              <div className={styles.dot} style={{ backgroundColor: legendItemColor }}></div>
              <div>
                <span className={styles.legendItemText}>{`${label}:`}</span>
                <span className={styles.legendItemValue}>{formattedLegendItemValue}</span>
              </div>
            </div>
            {metricsData && (
              <div className={cn(styles.metricsWrapper, { [styles.singleColumn]: label === 'Video' })}>
                {metrics &&
                  (metrics as Metric[]).map((metric) => {
                    // Temporarily disabled all interactive metrics EXCEPT impressions
                    if (
                      shouldPartiallyHideInteractiveMetrics &&
                      metric.label !== 'page.campaignDetails.performanceTab.keyMetrics.impressions'
                    ) {
                      return null;
                    }

                    return (
                      <div key={metric.label}>
                        <span className={styles.legendItemText}>{`${t(metric.label)}:`}</span>
                        <span className={styles.legendItemValue}>{`${getFormattedValue(metric)}`}</span>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default ChartLegend;
